'use client'

import { useRef, useState } from 'react'
import { Image, PromoBanner } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { useIsDarkMode } from '@marketplace-web/dark-mode/dark-mode-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  PromotionalListingBannerInCatalogFeedModel,
  clickEvent,
  viewEvent,
} from '@marketplace-web/banners/banners-data'
import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'

import { ITEM_UPLOAD_URL } from '../../constants/routes'

type Props = {
  banner: PromotionalListingBannerInCatalogFeedModel
  onClose: () => void
}

const PromotionalListingBanner = ({ banner, onClose }: Props) => {
  const [isClosed, setIsClosed] = useState(false)
  const isDarkMode = useIsDarkMode()
  const translate = useTranslate()
  const { track } = useTracking()
  const abTest = useAbTest(banner.abTest.name)
  const trackExpose = useTrackAbTestCallback()
  const wasExposed = useRef(false)

  const handleClick = () => {
    track(
      clickEvent({
        target: 'promotional_listing_banner',
        targetDetails: 'upload_item_promotional_listing_banner',
      }),
    )

    navigateToPage(ITEM_UPLOAD_URL)
  }

  const handleClose = () => {
    track(
      clickEvent({
        target: 'promotional_listing_banner',
        targetDetails: 'close_promotional_listing_banner',
      }),
    )

    setIsClosed(true)
    onClose()
  }

  const handleInViewChange = (inView: boolean) => {
    if (!inView) return
    if (!wasExposed.current) {
      trackExpose(abTest)
      wasExposed.current = true
    }

    if (banner.abTest.group.toLowerCase() === 'off') {
      return
    }

    track(viewEvent({ target: 'promotional_listing_banner' }))
  }

  const renderContent = () => {
    if (isClosed) return null
    if (!banner.title) return null
    if (!banner.body) return null
    if (!banner.actions) return null

    return (
      <PromoBanner
        icon={<Image src={isDarkMode ? banner.darkImageUrl : banner.imageUrl} size="medium" />}
        title={banner.title}
        clickable
        onClick={handleClick}
        closable
        closeButtonAriaLabel={translate('promotional_listing_banner.a11y.actions.close')}
        onClose={handleClose}
        body={banner.body}
        actionText={banner.actions.primary.title}
        styling="tight"
        testId="promotional-listing-banner"
      />
    )
  }

  return (
    <InView onChange={handleInViewChange} threshold={0.5}>
      {renderContent()}
    </InView>
  )
}

export default PromotionalListingBanner
